import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import {
  countNestedFigures, figureCss, repairNestedFigures, tableCss,
  TABLE_MIN_COL_WIDTH, TEXT_ALIGNS, type TextAlign as TextAlignValue,
} from '@blogaris/shared';
import { ResizableImage } from './ImageNode.js';
import { AlignedTableCell, AlignedTableHeader } from './table-cell-align.js';
import { TableGridPicker, TableToolbar } from './TableTools.js';
import {
  Button, cx,
  IconHeading, IconBold, IconItalic, IconStrike, IconCode, IconListBullet,
  IconListOrdered, IconQuote, IconLink, IconImage, IconMinus, IconUndo, IconRedo,
} from '../../components/ui/index.js';

/**
 * แถบเครื่องมือของ TipTap
 * ปุ่มทุกตัวเป็น type="button" — ถ้าเป็น submit จะไป submit ฟอร์มแม่โดยไม่ตั้งใจ
 */
function ToolbarButton({
  active, onClick, title, children, disabled,
}: { active?: boolean; onClick: () => void; title: string; children: React.ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button" onClick={onClick} title={title} aria-label={title} aria-pressed={active} disabled={disabled}
      className={cx(
        'grid h-8 min-w-8 place-items-center rounded-md px-2 text-sm transition-colors',
        active
          ? 'bg-[var(--brand)] text-[var(--brand-ink)]'
          : 'text-[var(--ink-2)] hover:bg-[var(--sunken)] hover:text-[var(--ink)]',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      {children}
    </button>
  );
}

/**
 * CSS ของรูปกับตารางมาจาก @blogaris/shared ตัวเดียวกับที่หน้าบล็อกจริงใช้
 * ใส่ผ่าน <style> ตอน runtime แทนที่จะก๊อปไปไว้ใน index.css
 * เพื่อไม่ให้ทั้งสองฝั่งค่อย ๆ เพี้ยนออกจากกัน
 */
const FIGURE_STYLE_ID = 'blogaris-figure-css';
function useFigureCss(): void {
  useEffect(() => {
    if (document.getElementById(FIGURE_STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = FIGURE_STYLE_ID;
    el.textContent = figureCss('.tiptap')
      + tableCss('.tiptap', { line: 'var(--line)', head: 'var(--sunken)', accent: 'var(--brand)' }, true);
    document.head.appendChild(el);
  }, []);
}

/**
 * ไอคอนจัดข้อความ — วาดเองแทนที่จะเพิ่มเข้าไปในชุดไอคอนหลัก
 * เพราะชุดนั้นสร้างจากฟอนต์ Flaticon ผ่านสคริปต์ ไม่ควรแก้ด้วยมือ
 */
function AlignIcon({ mode }: { mode: TextAlignValue }) {
  // ความยาวบรรทัดในแต่ละแบบ (บรรทัดที่ 2 กับ 4 สั้นกว่า เพื่อให้เห็นการจัดชิด)
  const rows = mode === 'justify' ? [14, 14, 14, 14] : [14, 9, 14, 9];
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
      {rows.map((w, i) => {
        const y = 3 + i * 3.2;
        const x = mode === 'right' ? 15 - w : mode === 'center' ? (16 - w) / 2 : 1;
        return <rect key={i} x={x} y={y} width={w} height="1.5" rx=".75" fill="currentColor" />;
      })}
    </svg>
  );
}

const ALIGN_LABELS: Record<TextAlignValue, string> = {
  left: 'ชิดซ้าย',
  center: 'กึ่งกลาง',
  right: 'ชิดขวา',
  justify: 'เต็มแนว',
};

function Divider() {
  return <span className="mx-1 h-5 w-px shrink-0 bg-[var(--line)]" aria-hidden="true" />;
}

function Toolbar({ editor, onPickImage }: { editor: Editor; onPickImage: () => void }) {
  const setLink = useCallback(() => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('ใส่ลิงก์ (เว้นว่างเพื่อลบลิงก์)', previous ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    // กัน javascript: ตั้งแต่ตอนพิมพ์ — ฝั่ง server กรองซ้ำอยู่แล้วแต่บอกผู้ใช้ตรงนี้ชัดกว่า
    if (!/^(https?:|mailto:|tel:|\/|#)/i.test(url)) {
      window.alert('รองรับเฉพาะลิงก์ที่ขึ้นต้นด้วย http://, https://, mailto:, tel: หรือ /');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 rounded-t-[14px] border-b border-[var(--line)] bg-[color-mix(in_srgb,var(--surface)_92%,transparent)] px-2 py-1.5 backdrop-blur">
      <ToolbarButton title="หัวข้อใหญ่ (H2)" active={editor.isActive('heading', { level: 2 })}
                     onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        <span className="flex items-center gap-0.5"><IconHeading size={14} /><span className="text-[11px] font-bold">2</span></span>
      </ToolbarButton>
      <ToolbarButton title="หัวข้อย่อย (H3)" active={editor.isActive('heading', { level: 3 })}
                     onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        <span className="flex items-center gap-0.5"><IconHeading size={14} /><span className="text-[11px] font-bold">3</span></span>
      </ToolbarButton>
      <Divider />
      <ToolbarButton title="ตัวหนา" active={editor.isActive('bold')}
                     onClick={() => editor.chain().focus().toggleBold().run()}><IconBold size={15} /></ToolbarButton>
      <ToolbarButton title="ตัวเอียง" active={editor.isActive('italic')}
                     onClick={() => editor.chain().focus().toggleItalic().run()}><IconItalic size={15} /></ToolbarButton>
      <ToolbarButton title="ขีดฆ่า" active={editor.isActive('strike')}
                     onClick={() => editor.chain().focus().toggleStrike().run()}><IconStrike size={15} /></ToolbarButton>
      <ToolbarButton title="โค้ดในบรรทัด" active={editor.isActive('code')}
                     onClick={() => editor.chain().focus().toggleCode().run()}><IconCode size={15} /></ToolbarButton>
      <Divider />
      <ToolbarButton title="รายการแบบจุด" active={editor.isActive('bulletList')}
                     onClick={() => editor.chain().focus().toggleBulletList().run()}><IconListBullet size={15} /></ToolbarButton>
      <ToolbarButton title="รายการแบบตัวเลข" active={editor.isActive('orderedList')}
                     onClick={() => editor.chain().focus().toggleOrderedList().run()}><IconListOrdered size={15} /></ToolbarButton>
      <ToolbarButton title="ยกคำพูด" active={editor.isActive('blockquote')}
                     onClick={() => editor.chain().focus().toggleBlockquote().run()}><IconQuote size={15} /></ToolbarButton>
      <ToolbarButton title="บล็อกโค้ด" active={editor.isActive('codeBlock')}
                     onClick={() => editor.chain().focus().toggleCodeBlock().run()}><IconCode size={15} /></ToolbarButton>
      <Divider />
      {TEXT_ALIGNS.map((mode) => (
        <ToolbarButton
          key={mode}
          title={ALIGN_LABELS[mode]}
          // ค่าเริ่มต้นคือชิดซ้าย ย่อหน้าที่ยังไม่เคยตั้งค่าจึงต้องให้ปุ่มซ้ายติดไว้ด้วย
          active={editor.isActive({ textAlign: mode }) || (mode === 'left' && !TEXT_ALIGNS.some((a) => a !== 'left' && editor.isActive({ textAlign: a })))}
          onClick={() => editor.chain().focus().setTextAlign(mode).run()}
        >
          <AlignIcon mode={mode} />
        </ToolbarButton>
      ))}
      <Divider />
      <ToolbarButton title="ลิงก์" active={editor.isActive('link')} onClick={setLink}><IconLink size={15} /></ToolbarButton>
      <ToolbarButton title="แทรกรูปจากคลังไฟล์" onClick={onPickImage}><IconImage size={15} /></ToolbarButton>
      <TableGridPicker editor={editor} />
      <ToolbarButton title="เส้นคั่น" onClick={() => editor.chain().focus().setHorizontalRule().run()}><IconMinus size={15} /></ToolbarButton>
      <Divider />
      <ToolbarButton title="เลิกทำ (Ctrl+Z)" disabled={!editor.can().undo()}
                     onClick={() => editor.chain().focus().undo().run()}><IconUndo size={15} /></ToolbarButton>
      <ToolbarButton title="ทำซ้ำ (Ctrl+Shift+Z)" disabled={!editor.can().redo()}
                     onClick={() => editor.chain().focus().redo().run()}><IconRedo size={15} /></ToolbarButton>

      <span className="ml-auto pr-1 text-xs tabular-nums text-[var(--ink-3)]">
        {editor.storage.characterCount?.characters?.() ?? editor.getText().length} ตัวอักษร
      </span>
    </div>
  );
}

export interface InsertableImage {
  src: string;
  alt?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface RichEditorHandle {
  /** แทรกรูปตรงตำแหน่งเคอร์เซอร์ ไม่ใช่ต่อท้ายบทความ */
  insertImage: (img: InsertableImage) => void;
}

export const RichEditor = forwardRef<RichEditorHandle, {
  value: unknown;
  onChange: (json: unknown) => void;
  onPickImage: () => void;
  editable?: boolean;
}>(function RichEditor({ value, onChange, onPickImage, editable = true }, ref) {
  useFigureCss();
  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        codeBlock: { HTMLAttributes: { class: 'font-mono' } },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        protocols: ['http', 'https', 'mailto', 'tel'],
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
      ResizableImage.configure({ inline: false, allowBase64: false }),
      // resizable = ลากขอบคอลัมน์ปรับความกว้างได้เหมือน Word
      Table.configure({ resizable: true, cellMinWidth: TABLE_MIN_COL_WIDTH, allowTableNodeSelection: true }),
      TableRow,
      AlignedTableHeader,
      AlignedTableCell,
      // เต็มแนว (justify) กับข้อความไทยจะยืดช่องว่างระหว่างวรรค ไม่ใช่ระหว่างตัวอักษร
      // ใช้ได้ผลดีกับย่อหน้ายาว ๆ แต่ย่อหน้าสั้นจะเห็นช่องว่างกว้างผิดปกติ
      TextAlign.configure({ types: ['heading', 'paragraph'], alignments: [...TEXT_ALIGNS] }),
      Placeholder.configure({ placeholder: 'เริ่มเขียนบทความของคุณที่นี่…' }),
    ],
    content: (value as never) ?? '',
    // ทำให้ปุ่มบน toolbar เห็นสถานะล่าสุดเสมอ (ตัวหนา/หัวข้ออยู่หรือเปล่า)
    onUpdate: ({ editor }) => onChange(editor.getJSON()),
    editorProps: {
      attributes: {
        class: 'tiptap px-5 py-4',
        spellcheck: 'false',
      },
    },
    immediatelyRender: false,
  });

  // โหลดบทความคนละตัวเข้ามาใน component เดิม (เช่นกด "ดูเวอร์ชันเก่า") ต้องเปลี่ยนเนื้อหา
  const lastExternal = useRef<string>('');
  useEffect(() => {
    if (!editor) return;
    // เอกสารที่เขียนไว้ตอน schema ยังยอมให้ซ้อนรูปในรูปได้ ต้องคลี่ออกก่อนโหลด
    // ไม่งั้นจะกลายเป็นเนื้อหาที่ schema ปัจจุบันแก้ไม่ได้
    if (countNestedFigures(value) > 0) {
      const fixed = repairNestedFigures(value);
      lastExternal.current = JSON.stringify(fixed);
      editor.commands.setContent(fixed as never, false);
      onChange(fixed);
      return;
    }
    const incoming = JSON.stringify(value ?? null);
    if (incoming === lastExternal.current) return;
    lastExternal.current = incoming;
    if (incoming !== JSON.stringify(editor.getJSON())) {
      // false = ไม่ยิง onUpdate ซ้ำ ไม่งั้นจะวนกลับมาเซ็ต state เดิมไม่รู้จบ
      editor.commands.setContent((value as never) ?? '', false);
    }
  }, [value, editor]);

  useImperativeHandle(ref, () => ({
    insertImage: (img) => {
      if (!editor) return;
      editor.chain().focus().insertContent({
        type: 'image',
        attrs: {
          src: img.src,
          alt: img.alt ?? '',
          align: 'center',
          width: null,
          naturalWidth: img.width ?? null,
          naturalHeight: img.height ?? null,
        },
      }).run();
    },
  }), [editor]);

  if (!editor) {
    return <div className="skeleton h-[480px] rounded-[14px]" />;
  }

  return (
    <div className="surface">
      {editable && <Toolbar editor={editor} onPickImage={onPickImage} />}
      {/* แถบจัดการตารางโผล่เฉพาะตอนเคอร์เซอร์อยู่ในตาราง — วางเป็นแถวใต้แถบหลัก
          แทนที่จะลอยทับเนื้อหา เพราะปุ่มมีเยอะและต้องกดหลายครั้งติดกัน */}
      {editable && editor.isActive('table') && <TableToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
});

/** ดึงข้อความล้วนจาก TipTap JSON — ใช้นับคำและสร้าง excerpt อัตโนมัติ */
export function textOf(doc: unknown): string {
  const parts: string[] = [];
  const walk = (n: any): void => {
    if (!n || typeof n !== 'object') return;
    if (typeof n.text === 'string') parts.push(n.text);
    if (Array.isArray(n.content)) n.content.forEach(walk);
  };
  walk(doc);
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

/**
 * จำว่าผู้ใช้เปิดหรือปิดบันทึกอัตโนมัติไว้
 * เก็บนอก React เพราะเป็นความชอบส่วนตัวที่ต้องคงอยู่ข้ามบทความและข้ามการเปิดใหม่
 */
const AUTOSAVE_PREF_KEY = 'blogaris.autosave';

export function readAutosavePref(): boolean {
  try { return localStorage.getItem(AUTOSAVE_PREF_KEY) !== 'off'; }
  catch { return true; }
}

export function writeAutosavePref(on: boolean): void {
  try { localStorage.setItem(AUTOSAVE_PREF_KEY, on ? 'on' : 'off'); }
  catch { /* localStorage ถูกปิด — ใช้ค่าปัจจุบันไปจนกว่าจะปิดแท็บ */ }
}

export function useAutosave<T>(
  value: T,
  save: (v: T) => Promise<void>,
  { delayMs = 20_000, enabled = true, auto = true }:
    { delayMs?: number; enabled?: boolean; auto?: boolean } = {},
): {
  status: 'idle' | 'saving' | 'saved' | 'error';
  saveNow: () => void;
  lastSavedAt: Date | null;
  /** true เมื่อมีการแก้ไขที่ยังไม่ได้บันทึก — ใช้บอกผู้ใช้ตอนปิดบันทึกอัตโนมัติ */
  dirty: boolean;
} {
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);
  const savedSnapshot = useRef(JSON.stringify(value));
  const valueRef = useRef(value);
  valueRef.current = value;

  const run = useCallback(async () => {
    const snapshot = JSON.stringify(valueRef.current);
    if (snapshot === savedSnapshot.current) return;
    setStatus('saving');
    try {
      await save(valueRef.current);
      savedSnapshot.current = snapshot;
      setLastSavedAt(new Date());
      setStatus('saved');
      setDirty(false);
    } catch {
      setStatus('error');
    }
  }, [save]);

  // ติดตามว่ายังมีอะไรไม่ได้บันทึกอยู่ไหม สำคัญเป็นพิเศษตอนปิดบันทึกอัตโนมัติ
  useEffect(() => {
    setDirty(JSON.stringify(value) !== savedSnapshot.current);
  }, [value]);

  /** ข้อ 9 — autosave ทุก 20 วินาที (ปิดได้จากสวิตช์ในแถบบน) */
  useEffect(() => {
    if (!enabled || !auto) return;
    const timer = setInterval(() => void run(), delayMs);
    return () => clearInterval(timer);
  }, [run, delayMs, enabled, auto]);

  // Ctrl/Cmd+S = บันทึกทันที (นักเขียนกดกันโดยสัญชาตญาณ)
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void run();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [run, enabled]);

  // เตือนก่อนปิดแท็บถ้ายังมีอะไรไม่ได้บันทึก
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (JSON.stringify(valueRef.current) !== savedSnapshot.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  return { status, saveNow: () => void run(), lastSavedAt, dirty };
}

/**
 * ข้อ 9 — กู้ draft จาก localStorage เมื่อปิดเบราว์เซอร์
 * เก็บแยกตาม postId เพื่อไม่ให้บทความคนละเรื่องทับกัน และล้างทิ้งเมื่อบันทึกขึ้น
 * server สำเร็จแล้ว เพื่อไม่ให้ค้างเป็นขยะและไม่ให้เด้งถามซ้ำ
 */
export function useLocalDraft<T>(key: string, value: T, enabled = true) {
  const storageKey = `blogaris.draft.${key}`;
  const [recovered, setRecovered] = useState<T | null>(null);

  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { at: number; value: T };
        // draft เกิน 7 วันถือว่าเก่าเกินจะมีประโยชน์
        if (Date.now() - parsed.at < 7 * 86400_000) setRecovered(parsed.value);
        else localStorage.removeItem(storageKey);
      }
    } catch { /* localStorage ถูกปิด */ }
    // ตั้งใจอ่านครั้งเดียวตอน mount — ไม่งั้นจะเด้งถามซ้ำทุกครั้งที่พิมพ์
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const timer = setTimeout(() => {
      try { localStorage.setItem(storageKey, JSON.stringify({ at: Date.now(), value })); }
      catch { /* เต็ม */ }
    }, 1200);
    return () => clearTimeout(timer);
  }, [value, storageKey, enabled]);

  const clear = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    setRecovered(null);
  }, [storageKey]);

  return { recovered, discard: clear, clear };
}
